import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Brain, Database, Loader2, ExternalLink, ChevronRight } from 'lucide-react';
import { aiActApi } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function GpaiModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiActApi.listSystems({ entity_type: 'gpai_model' });
      setModels(res.data);
    } catch { toast.error('Erro ao carregar modelos GPAI'); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/seed-ai-gpai');
      toast.success(`Catálogo: ${res.data.created} criados, ${res.data.skipped} ignorados`);
      load();
    } catch { toast.error('Erro ao seed catálogo'); }
    finally { setSeeding(false); }
  };

  if (loading) return <Layout title="EU AI Act — Modelos GPAI"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Modelos GPAI (Arts. 51-56)" headerContent={
      <Button onClick={handleSeed} disabled={seeding} data-testid="gpai-seed-btn">
        {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
        Importar catálogo
      </Button>
    }>
      <div className="space-y-6" data-testid="gpai-page">
        <Card className="card-flat bg-indigo-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Brain className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="font-medium text-indigo-900">Modelos de IA de Propósito Geral</p>
                <p className="text-sm text-indigo-700 mt-1">
                  Modelos GPAI estão sujeitos aos Arts. 51-56 do AI Act: documentação técnica (Art. 53), representante UE (Art. 54),
                  obrigações adicionais para modelos com risco sistémico (Art. 55) e códigos de prática (Art. 56).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {models.length === 0 ? (
          <Card className="card-flat"><CardContent className="p-8 text-center">
            <Brain className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">Nenhum modelo GPAI registado</p>
            <Button onClick={handleSeed} disabled={seeding} data-testid="seed-empty-btn">
              <Database className="h-4 w-4 mr-2" />Importar catálogo de modelos conhecidos
            </Button>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="gpai-list">
            {models.map(m => (
              <Link key={m.system_id} to={`/ai-act/systems/${m.system_id}`}>
                <Card className="card-flat hover:shadow-md transition-shadow cursor-pointer" data-testid={`gpai-${m.system_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-12 w-12 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                        <Brain className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">GPAI</Badge>
                          {m.ai_conformity_status === 'registered_eu' && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">EU Reg ✓</Badge>
                          )}
                        </div>
                        <p className="font-medium text-slate-900 truncate">{m.name}</p>
                        <p className="text-xs text-slate-500 truncate">{m.provider} {m.version && `• ${m.version}`}</p>
                        {m.ai_purpose && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{m.ai_purpose}</p>}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
